import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Box,
  Typography,
  Divider,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { barnsService, Barn, BarnContact, BarnFormData } from '../../services/barnsService';

interface BarnFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, the dialog edits this barn; otherwise it creates a new one. */
  barn?: Barn | null;
  onSaved?: (barn: Barn) => void;
}

// Suggestions only — the role field is free-text so clinics aren't boxed in.
// Farrier replaced Groom, Rider dropped — per Ali 2026-07-08 (existing saved
// roles are unaffected; this list is just the autocomplete).
const ROLE_SUGGESTIONS = [
  'Barn Manager',
  'Trainer',
  'Owner',
  'Farrier',
  'Emergency Contact',
];

const emptyForm: BarnFormData = {
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contacts: [],
  notes: '',
};

const blankContact: BarnContact = { name: '', role: '', phone: '', email: '' };

const BarnFormDialog: FC<BarnFormDialogProps> = ({ open, onClose, barn, onSaved }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BarnFormData>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm(
        barn
          ? {
              name: barn.name,
              street: barn.street ?? '',
              city: barn.city ?? '',
              state: barn.state ?? '',
              zip: barn.zip ?? '',
              contactName: barn.contactName ?? '',
              contactEmail: barn.contactEmail ?? '',
              contactPhone: barn.contactPhone ?? '',
              contacts: (barn.contacts ?? []).map((c) => ({
                name: c.name ?? '',
                role: c.role ?? '',
                phone: c.phone ?? '',
                email: c.email ?? '',
              })),
              notes: barn.notes ?? '',
            }
          : emptyForm,
      );
    }
  }, [open, barn]);

  const set = (field: keyof BarnFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setContact = (index: number, field: keyof BarnContact, value: string) =>
    setForm((f) => {
      const contacts = [...(f.contacts ?? [])];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...f, contacts };
    });

  const addContact = () =>
    setForm((f) => ({ ...f, contacts: [...(f.contacts ?? []), { ...blankContact }] }));

  const removeContact = (index: number) =>
    setForm((f) => ({ ...f, contacts: (f.contacts ?? []).filter((_, i) => i !== index) }));

  const mutation = useMutation(
    async () => {
      // Create: empty optional fields are dropped (so @IsEmail never sees '').
      // Edit: a cleared field must be sent as an EXPLICIT null — an omitted key
      // means "leave unchanged" to the API, so dropping it made clearing a
      // field a silent no-op.
      const payload: BarnFormData = { name: form.name.trim() };
      (['street', 'city', 'state', 'zip', 'contactName', 'contactEmail', 'contactPhone', 'notes'] as const).forEach(
        (key) => {
          const value = form[key]?.trim();
          if (value) payload[key] = value;
          else if (barn) payload[key] = null;
        },
      );
      // Additional contacts: keep only rows with a name, trim every field, and
      // drop blank optional fields (so an empty email never fails @IsEmail).
      // Always send the array (even empty) so removing every contact persists.
      payload.contacts = (form.contacts ?? [])
        .map((c) => {
          const entry: BarnContact = { name: (c.name ?? '').trim() };
          const role = c.role?.trim();
          const phone = c.phone?.trim();
          const email = c.email?.trim();
          if (role) entry.role = role;
          if (phone) entry.phone = phone;
          if (email) entry.email = email;
          return entry;
        })
        .filter((c) => c.name.length > 0);
      return barn ? barnsService.update(barn.id, payload) : barnsService.create(payload);
    },
    {
      onSuccess: (saved) => {
        queryClient.invalidateQueries(['barns']);
        queryClient.invalidateQueries(['barn', saved.id]);
        // Barn renames change the location strings the patient form + Barn
        // Operations autocomplete over.
        queryClient.invalidateQueries(['horse-locations']);
        queryClient.invalidateQueries(['horses']);
        onSaved?.(saved);
        onClose();
      },
      onError: (err: any) => {
        const message = err?.response?.data?.message;
        setError(Array.isArray(message) ? message.join('. ') : message ?? 'Failed to save barn');
      },
    },
  );

  const contacts = form.contacts ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{barn ? 'Edit Barn' : 'Add Barn'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Barn name"
              value={form.name}
              onChange={set('name')}
              fullWidth
              required
              autoFocus
              inputProps={{ maxLength: 120 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Street" value={form.street} onChange={set('street')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="City" value={form.city} onChange={set('city')} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="State" value={form.state} onChange={set('state')} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="ZIP" value={form.zip} onChange={set('zip')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Primary contact name"
              value={form.contactName}
              onChange={set('contactName')}
              fullWidth
              placeholder="Barn manager"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Primary contact phone"
              value={form.contactPhone}
              onChange={set('contactPhone')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Primary contact email"
              type="email"
              value={form.contactEmail}
              onChange={set('contactEmail')}
              fullWidth
            />
          </Grid>

          {/* Additional contacts — barn manager, trainer, etc. */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Additional contacts</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addContact}>
                Add contact
              </Button>
            </Box>
            {contacts.length === 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Add a barn manager, trainer, or other point of contact.
              </Typography>
            )}
          </Grid>

          {contacts.map((contact, index) => (
            <Grid item xs={12} key={index}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={ROLE_SUGGESTIONS}
                      value={contact.role ?? ''}
                      onChange={(_e, value) => setContact(index, 'role', value ?? '')}
                      onInputChange={(_e, value) => setContact(index, 'role', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Role" placeholder="Barn Manager" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={11} sm={5}>
                    <TextField
                      label="Name"
                      size="small"
                      value={contact.name}
                      onChange={(e) => setContact(index, 'name', e.target.value)}
                      fullWidth
                      required
                      inputProps={{ maxLength: 120 }}
                    />
                  </Grid>
                  <Grid item xs={1} sm={1} sx={{ textAlign: 'right' }}>
                    <IconButton
                      size="small"
                      aria-label="Remove contact"
                      onClick={() => removeContact(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone"
                      size="small"
                      value={contact.phone}
                      onChange={(e) => setContact(index, 'phone', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      type="email"
                      size="small"
                      value={contact.email}
                      onChange={(e) => setContact(index, 'email', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}

          <Grid item xs={12}>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={set('notes')}
              fullWidth
              multiline
              minRows={2}
              placeholder="Directions, gate codes, feeding notes…"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => mutation.mutate()}
          variant="contained"
          disabled={!form.name.trim() || mutation.isLoading}
        >
          {barn ? 'Save' : 'Add Barn'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarnFormDialog;
